import React, { useRef } from 'react';
import { Box, TextField, Tooltip, Typography, Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import {
  Step2CardsContainer,
  Step2Card,
  InfoCardTitle,
  FieldGroup,
  FieldLabel,
  FieldHint,
  ImageUploadArea,
  ImageUploadIcon,
  ImageUploadText,
  SmallUploadButton,
  ImagePreviewWrapper,
  ImagePreview,
  ImageRemoveButton,
  AIToggleBoxNew,
  AIToggleContent,
  AIIcon,
  AIToggleLabelNew,
  StyledSwitch,
  RichTextContainer,
  BottomActionsNew,
  CancelButtonNew,
  PrevButtonNew,
  SaveButtonNew,
  ImageUploadBox,
} from './TicketEditStyled';
import { styled } from '@mui/material/styles';

// 추가 스타일 컴포넌트
const ImageGridContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginTop: '16px',
});

const SmallImagePreview = styled(Box)({
  position: 'relative',
  width: '80px',
  height: '80px',
  borderRadius: '4px',
  overflow: 'hidden',
});

const SmallImageRemoveButton = styled(Box)({
  position: 'absolute',
  top: '4px',
  right: '4px',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#fff',
});

const ImageCountText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
  marginTop: '8px',
});

const ImageInfoText = styled(Typography)({
  fontSize: '12px',
  color: '#A6A6A6',
  marginTop: '8px',
  lineHeight: 1.5,
});

const CurriculumCard = styled(Box)({
  background: '#F3F9FA',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const CurriculumHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const CurriculumDeleteIcon = styled(Box)({
  color: '#F87171',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
});

const CurriculumRow = styled(Box)({
  display: 'flex',
  gap: '16px',
});

const CurriculumLeftSection = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const CurriculumRightSection = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const AddCurriculumButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  color: '#404040',
  '&:hover': {
    opacity: 0.7,
  },
});

const CurriculumInfoBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '8px',
});

function TicketEditStep02({
  aiToggle,
  setAiToggle,
  voucherImages,
  handleVoucherImageUpload,
  handleRemoveVoucherImage,
  curList,
  handleAddCurriculum,
  handleRemoveCurriculum,
  handleCurriculumChange,
  handleCurriculumImageUpload,
  handleRemoveCurriculumImage,
  classDetail,
  setClassDetail,
  onPrev,
  onSubmit,
  onCancel,
  loader,
  isEditMode,
  onDelete,
  canSave = true,
  exposureYn,
  setExposureYn,
}) {
  const voucherImageInputRef = useRef(null);

  return (
    <>
      <Step2CardsContainer>
        {/* Card 1: 수강권 이미지 */}
        <Step2Card width="360px">
          <InfoCardTitle>수강권 이미지</InfoCardTitle>

          {/* 이미지 업로드 영역 */}
          {voucherImages.length === 0 ? (
            <ImageUploadArea onClick={() => voucherImageInputRef.current?.click()}>
              <ImageUploadIcon>
                <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: '#7DC8CF' }} />
              </ImageUploadIcon>
              <ImageUploadText>
                클릭하여 업로드 또는
                <br />
                드래그 앤 드롭
              </ImageUploadText>
            </ImageUploadArea>
          ) : (
            <Box>
              {/* 메인 이미지 프리뷰 */}
              <ImagePreviewWrapper sx={{ width: '100%', height: '200px' }}>
                <ImagePreview src={voucherImages[0]?.preview} alt="main-voucher" />
                <ImageRemoveButton onClick={() => handleRemoveVoucherImage(0)}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </ImageRemoveButton>
              </ImagePreviewWrapper>
            </Box>
          )}

          {/* 추가 이미지 그리드 */}
          <ImageGridContainer>
            {voucherImages.slice(1).map((img, index) => (
              <SmallImagePreview key={index + 1}>
                <img
                  src={img.preview}
                  alt={`voucher-${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <SmallImageRemoveButton onClick={() => handleRemoveVoucherImage(index + 1)}>
                  <CloseIcon sx={{ fontSize: 10 }} />
                </SmallImageRemoveButton>
              </SmallImagePreview>
            ))}

            {/* 추가 업로드 버튼 */}
            {voucherImages.length > 0 && voucherImages.length < 7 && (
              <SmallUploadButton onClick={() => voucherImageInputRef.current?.click()}>
                <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#A6A6A6' }} />
              </SmallUploadButton>
            )}
          </ImageGridContainer>

          <input
            type="file"
            accept="image/*"
            multiple
            ref={voucherImageInputRef}
            style={{ display: 'none' }}
            onChange={handleVoucherImageUpload}
          />

          <ImageCountText>{voucherImages.length}/7</ImageCountText>
          <ImageInfoText>
            이미지는 최대 7장, PNG/JPEG파일. 파일크기 최대 8MB, 가로세로 비율은 4:3, 1200*900px 사이즈를 권장합니다.
          </ImageInfoText>
        </Step2Card>

        {/* Card 2: 커리큘럼 등록 */}
        <Step2Card width="420px">
          <InfoCardTitle>커리큘럼 등록</InfoCardTitle>

          {/* AI로 작성하기 - 주석 처리 */}
          {/* <AIToggleBoxNew>
            <AIToggleContent>
              <AIIcon>
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              </AIIcon>
              <AIToggleLabelNew>AI로 작성하기</AIToggleLabelNew>
            </AIToggleContent>
            <StyledSwitch checked={aiToggle} onChange={e => setAiToggle(e.target.checked)} />
          </AIToggleBoxNew> */}

          {curList.map((cur, curIndex) => (
            <CurriculumCard key={curIndex}>
              {curList.length > 1 && (
                <CurriculumHeader>
                  <FieldLabel>커리큘럼 {curIndex + 1}</FieldLabel>
                  <CurriculumDeleteIcon onClick={() => handleRemoveCurriculum(curIndex)}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </CurriculumDeleteIcon>
                </CurriculumHeader>
              )}

              {/* 제목 */}
              <FieldGroup>
                <FieldLabel>제목</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="제목을 입력해 주세요."
                  value={cur.title}
                  onChange={e => handleCurriculumChange(curIndex, 'title', e.target.value)}
                  sx={{
                    background: '#fff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                    },
                  }}
                />
              </FieldGroup>

              {/* 사진 업로드 */}
              <FieldGroup>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FieldLabel>사진 업로드</FieldLabel>
                  <FieldHint>*최대 3장</FieldHint>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <ImageUploadBox as="label" sx={{ width: '80px', height: '80px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={e => handleCurriculumImageUpload(curIndex, e)}
                    />
                    <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#A6A6A6' }} />
                  </ImageUploadBox>
                  {(cur.images || []).map((img, imgIndex) => (
                    <Box key={imgIndex} sx={{ position: 'relative', width: '80px', height: '80px' }}>
                      <img
                        src={img.preview}
                        alt={`cur-${curIndex}-${imgIndex}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                      />
                      <SmallImageRemoveButton onClick={() => handleRemoveCurriculumImage(curIndex, imgIndex)}>
                        <CloseIcon sx={{ fontSize: 10 }} />
                      </SmallImageRemoveButton>
                    </Box>
                  ))}
                </Box>
              </FieldGroup>

              {/* 내용 */}
              <FieldGroup>
                <FieldLabel>내용</FieldLabel>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={3}
                  placeholder="내용을 입력해 주세요."
                  value={cur.outline}
                  onChange={e => handleCurriculumChange(curIndex, 'outline', e.target.value)}
                  sx={{
                    background: '#fff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      minHeight: '80px',
                      alignItems: 'flex-start',
                    },
                  }}
                />
              </FieldGroup>
            </CurriculumCard>
          ))}

          {/* 커리큘럼 추가 버튼 */}
          <AddCurriculumButton onClick={handleAddCurriculum} sx={{ justifyContent: 'center', width: '100%' }}>
            <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>커리큘럼 추가</Typography>
          </AddCurriculumButton>
        </Step2Card>

        {/* Card 3: 수강권 상세 */}
        <Step2Card sx={{ flex: 1 }}>
          <InfoCardTitle>수강권 상세</InfoCardTitle>

          {/* AI 토글 */}
          {/* <AIToggleBoxNew>
            <AIToggleContent>
              <AIIcon>
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              </AIIcon>
              <AIToggleLabelNew>AI로 작성하기</AIToggleLabelNew>
            </AIToggleContent>
            <StyledSwitch checked={aiToggle} onChange={e => setAiToggle(e.target.checked)} />
          </AIToggleBoxNew> */}

          {/* 내용 */}
          <FieldGroup sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <FieldLabel>내용</FieldLabel>
            <RichTextContainer sx={{ flex: 1 }}>
              <CKEditor
                editor={ClassicEditor}
                data={classDetail}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setClassDetail(data);
                }}
                config={{
                  placeholder: '수강권 상세 내용을 입력해 주세요',
                }}
              />
            </RichTextContainer>
          </FieldGroup>
        </Step2Card>
      </Step2CardsContainer>

      {/* Bottom Buttons */}
      <BottomActionsNew>
        <CancelButtonNew onClick={onCancel}>취소</CancelButtonNew>
        <PrevButtonNew onClick={onPrev}>이전</PrevButtonNew>
        <Tooltip title={!canSave ? '권한이 없습니다' : ''} arrow>
          <span>
            <SaveButtonNew onClick={onSubmit} disabled={loader || !canSave}>
              {isEditMode ? '저장' : '등록'}
            </SaveButtonNew>
          </span>
        </Tooltip>
      </BottomActionsNew>
    </>
  );
}

export default TicketEditStep02;
